import { Badge } from '@morressier/alembic';
import { Box, Flex, Tooltip } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDiagramSubtask,
  faFileLines,
  faLanguage
} from '@fortawesome/pro-regular-svg-icons';
import { GenericSubmissionField } from './FormFieldGeneric/GenericSubmissionField';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { CommandResponseProvider } from 'src/contexts/SourceFileCommandResponseContex';
import GroupRenderer from './FormFieldGeneric/GroupRenderer';
import { OutlineCard as AlembicOutlineCard } from '@morressier/alembic';
import { PreferencesTab } from './PreferencesTab';

export const OutlineCard: React.FC<{ children: React.ReactNode }> = ({
  children
}) => (
  <AlembicOutlineCard
    borderless
    borderRadius="none"
    flexDirection="column"
    justifyContent="space-between"
    padding={4}
    gap={4}
  >
    {children}
  </AlembicOutlineCard>
);
interface Props {
  isUpdateRequired?: boolean;
  isRevisionReview?: boolean;
}
export const SubmissionTab: React.FC<Props> = ({
  isUpdateRequired = true,
  isRevisionReview = false
}) => {
  const {
    fieldsResponse: { articleTypeName, fields },
    workflowResponse: { workflowData: { currentState } }
  } = useSubmissionData();
  const isUnsubmitted = currentState === 'unsubmitted';

  const articleSubType = fields['ArticleSubtype']?.value as string | undefined;
  const articleLanguage = fields['ArticleLanguage']?.value as
    | string
    | undefined;

  return (
    <Flex flexDirection="column" gap={4}>
      {((!isUpdateRequired && !isRevisionReview) || isUnsubmitted) && (
        <Box>
          <Badge variant="attention" size="context">
            <Flex gap={1} alignItems="center">
              <FontAwesomeIcon icon={faFileLines} />
              {articleTypeName}
            </Flex>
          </Badge>
        </Box>
      )}
      <Flex flexDirection="row" gap={2}>
        {articleSubType && (
          <Tooltip
            hasArrow
            placement="bottom"
            label={`Article Sub Type`}
            borderRadius={4}
            shouldWrapChildren
          >
            <Badge variant="interactive" size="context">
              <Flex gap={1} alignItems="center">
                <FontAwesomeIcon icon={faDiagramSubtask} />
                {articleSubType}
              </Flex>
            </Badge>
          </Tooltip>
        )}
        {articleLanguage && (
          <Tooltip
            hasArrow
            placement="bottom"
            label={`Article Language`}
            borderRadius={4}
            shouldWrapChildren
          >
            <Badge variant="success" size="context">
              <Flex gap={1} alignItems="center">
                <FontAwesomeIcon icon={faLanguage} />
                {articleLanguage}
              </Flex>
            </Badge>
          </Tooltip>
        )}
      </Flex>

      <GenericSubmissionField
        isUpdateRequired={isUpdateRequired}
        isRevisionReview={isRevisionReview}
        fieldName="FocusIssue"
      />

      <GenericSubmissionField
        isUpdateRequired={isUpdateRequired}
        isRevisionReview={isRevisionReview}
        fieldName="Title"
      />
      <GenericSubmissionField
        isUpdateRequired={isUpdateRequired}
        isRevisionReview={isRevisionReview}
        fieldName="EnglishTitle"
      />
      <GenericSubmissionField
        isUpdateRequired={isUpdateRequired}
        isRevisionReview={isRevisionReview}
        fieldName="Subtitle"
      />
      <GenericSubmissionField
        isUpdateRequired={isUpdateRequired}
        isRevisionReview={isRevisionReview}
        fieldName="EnglishSubtitle"
      />
      <GenericSubmissionField
        isUpdateRequired={isUpdateRequired}
        isRevisionReview={isRevisionReview}
        fieldName="Abstract"
      />
      <GenericSubmissionField
        isUpdateRequired={isUpdateRequired}
        isRevisionReview={isRevisionReview}
        fieldName="EnglishAbstract"
      />
      <GenericSubmissionField
        isUpdateRequired={isUpdateRequired}
        isRevisionReview={isRevisionReview}
        fieldName="Keywords"
      />

      <GenericSubmissionField
        isUpdateRequired={isUpdateRequired}
        isRevisionReview={isRevisionReview}
        fieldName="SubmissionGuidelines"
      />
      <GenericSubmissionField
        isUpdateRequired={isUpdateRequired}
        isRevisionReview={isRevisionReview}
        fieldName="EnglishKeywords"
      />
      <GenericSubmissionField
        isUpdateRequired={isUpdateRequired}
        isRevisionReview={isRevisionReview}
        fieldName="ArticleSubtype"
      />
      <GenericSubmissionField
        isUpdateRequired={false}
        isRevisionReview={isRevisionReview}
        fieldName="ArticleLanguage"
      />
      <GenericSubmissionField
        isUpdateRequired={isUpdateRequired}
        isRevisionReview={isRevisionReview}
        fieldName="Category"
      />
      <GenericSubmissionField
        isUpdateRequired={isUpdateRequired}
        isRevisionReview={isRevisionReview}
        fieldName="CategoryOther"
      />
      <GenericSubmissionField
        isUpdateRequired={isUpdateRequired}
        isRevisionReview={isRevisionReview}
        fieldName="FiguresAndTables"
      />

      <Flex flexDirection="column" gap={4} id="Submission Files">
        <Flex flexDirection={'column'} gap={4}>
          <CommandResponseProvider>
            <GenericSubmissionField
              isUpdateRequired={isUpdateRequired}
              isRevisionReview={isRevisionReview}
              fieldName="SourceFiles"
            />
          </CommandResponseProvider>
          {!isUpdateRequired && !isRevisionReview && (
            <>
              <GenericSubmissionField
                isUpdateRequired={isUpdateRequired}
                isRevisionReview={isRevisionReview}
                fieldName="Manuscript"
              />
              <GenericSubmissionField
                isUpdateRequired={isUpdateRequired}
                isRevisionReview={isRevisionReview}
                fieldName="TitlePage"
              />
              <GenericSubmissionField
                isUpdateRequired={isUpdateRequired}
                isRevisionReview={isRevisionReview}
                fieldName="ConsolidatedPdf"
              />
              <GenericSubmissionField
                isUpdateRequired={isUpdateRequired}
                isRevisionReview={isRevisionReview}
                fieldName="AnonymizedConsolidatedPdf"
              />
            </>
          )}
          <GenericSubmissionField
            isUpdateRequired={isUpdateRequired}
            isRevisionReview={isRevisionReview}
            fieldName="CompleteDocumentForReview"
          />
          <GenericSubmissionField
            isUpdateRequired={isUpdateRequired}
            isRevisionReview={isRevisionReview}
            fieldName="AnonymizedManuscript"
          />
          <GenericSubmissionField
            isUpdateRequired={isUpdateRequired}
            isRevisionReview={isRevisionReview}
            fieldName="Supplementary"
            showSubtitle={false}
          />
          <GenericSubmissionField
            isUpdateRequired={isUpdateRequired}
            isRevisionReview={isRevisionReview}
            fieldName="AdditionalSupplementary"
            showSubtitle={false}
          />

          <GroupRenderer
            isUpdateRequired={isUpdateRequired}
            isRevisionReview={isRevisionReview}
            showSubtitle={false}
            fields={[
              {
                name: 'CoverLetter',
                renderMethod: element => element
              },
              {
                name: 'StandardReportingGuidelines',
                renderMethod: el => el
              }
            ]}
          >
            {renderedFields => (
              <Flex gap={4} flexDirection="column">
                {renderedFields}
              </Flex>
            )}
          </GroupRenderer>
          <GroupRenderer
            isUpdateRequired={isUpdateRequired}
            isRevisionReview={isRevisionReview}
            fields={[
              {
                name: 'Figures',
                renderMethod: el => el
              },
              {
                name: 'GraphicalAbstractImage',
                renderMethod: el => el
              },
              {
                name: 'Tables',
                renderMethod: el => el
              }
            ]}
          >
            {renderedFields => (
              <Flex gap={4} flexDirection="column">
                {renderedFields}
              </Flex>
            )}
          </GroupRenderer>
          <GenericSubmissionField
            isUpdateRequired={isUpdateRequired}
            isRevisionReview={isRevisionReview}
            fieldName="NumberOfFigures"
          />
          <GenericSubmissionField
            isUpdateRequired={isUpdateRequired}
            isRevisionReview={isRevisionReview}
            fieldName="NumberOfColorFigures"
          />
          <GenericSubmissionField
            isUpdateRequired={isUpdateRequired}
            isRevisionReview={isRevisionReview}
            fieldName="NumberOfTables"
          />
          <GenericSubmissionField
            isUpdateRequired={isUpdateRequired}
            isRevisionReview={isRevisionReview}
            fieldName="NumberOfWords"
          />
          <GenericSubmissionField
            isUpdateRequired={isUpdateRequired}
            isRevisionReview={isRevisionReview}
            fieldName="GeneralClassifications"
          />
        </Flex>
        {!isUpdateRequired && !isRevisionReview && (
          <PreferencesTab
            showTitle={false}
            isShow={true}
            isUpdateRequired={isUpdateRequired}
            isRevisionReview={isRevisionReview}
          />
        )}
      </Flex>
    </Flex>
  );
};
